import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import api from '../lib/api';

import { User, SubscriptionIssue } from '@/types';
import { API_ROUTES } from '@/constants';

export interface AuthContextData {
  user: User | null;
  loading: boolean;
  subscriptionIssue: SubscriptionIssue | null;
  login: (username: string, password: string, remember?: boolean) => Promise<User>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData | null>(null);

export const formatApiErrorDetail = (detail: unknown): string => {
  if (detail == null) return 'Algo deu errado. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (typeof detail === 'object' && detail && 'msg' in detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [subscriptionIssue, setSubscriptionIssue] = useState<SubscriptionIssue | null>(null);

  useEffect(() => {
    const handleSubscriptionBlocked = (event: Event) => {
      const detail = (event as CustomEvent<SubscriptionIssue>).detail;
      setSubscriptionIssue(detail);
    };
    window.addEventListener('subscriptionBlocked', handleSubscriptionBlocked);
    return () => window.removeEventListener('subscriptionBlocked', handleSubscriptionBlocked);
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await api.get(API_ROUTES.AUTH.ME, {
        timeout: 5000, // 5 seconds timeout to avoid hanging on bad cookies
      });
      setUser(data);
      setSubscriptionIssue(null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string, remember: boolean = false) => {
    const { data } = await api.post(
      API_ROUTES.AUTH.LOGIN,
      { username, password, remember }
    );
    setUser(data);
    setSubscriptionIssue(null);
    return data;
  };

  const logout = async () => {
    try {
      await api.post(API_ROUTES.AUTH.LOGOUT, {});
    } catch {
      // Erro de logout silenciado
    }
    setUser(null);
    setSubscriptionIssue(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, subscriptionIssue, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
